import { createSelector } from 'reselect';

import { createSelectorFactory } from 'wanderu-ducks-utils';

import { selectAppStateSlice } from '../selectors';
import NAMESPACE from './types';

function _selectData(appState) {
    return selectAppStateSlice(appState)[NAMESPACE];
}

function _selectRawCarrierData(appState, carrierCode) {
    return _selectData(appState)[carrierCode];
}

function _mapCarrierData(carrierData) {
    if (!carrierData) {
        return undefined;
    }

    const {
        carrier_id,
        features,
        livedetails,
        name,
        supplementalinfo,
        ticketDelivery,
        vehicle_type,
        book,
        preferred_currency,
        canonical_slug,
        groups = {},
    } = carrierData;
    const {
        business,
    } = groups;

    const carrierFeatures = features && Object.entries(features)
        .reduce((featureSet, [key, value]) => {
            return value
                ? featureSet.concat(key)
                : featureSet;
        }, []);

    return {
        carrierCode: carrier_id,
        businessGroup: business,
        features: carrierFeatures,
        liveDetails: livedetails,
        name,
        supplementalInfo: supplementalinfo,
        ticketDelivery,
        vehicleType: vehicle_type,
        preferredCurrency: preferred_currency,
        maxSeats: book
            ? book.seats_max
            : 1,
        pageSlug: canonical_slug,
    };
}

function _mapMultipleCarrierData(data, carrierCodes) {
    return carrierCodes.reduce((map, carrierCode) => ({
        ...map,
        [carrierCode]: _mapCarrierData(data[carrierCode]),
    }), {});
}

function selectCarrierDataFactory(carrierCodes) {
    if (!carrierCodes) {
        return undefined;
    }

    if (typeof carrierCodes === 'string') {
        const carrierCode = carrierCodes;
        return createSelector(
            (state) => _selectRawCarrierData(state, carrierCode),
            _mapCarrierData,
        );
    }

    return createSelector(
        _selectData,
        (data) => _mapMultipleCarrierData(data, carrierCodes),
    );
}
const selectCarrierData = createSelectorFactory(selectCarrierDataFactory);

const selectCarrierCollection = createSelector(
    _selectData,
    (allCarrierData) => {
        return Object.entries(allCarrierData)
            .reduce((collection, [code, rawData]) => ({
                ...collection,
                [code]: _mapCarrierData(rawData),
            }), {});
    },
);

function selectRequiresSupplementalTripInfo(appState, carrierCode) {
    const { supplementalInfo = {} } = selectCarrierData(appState, carrierCode) || {};
    return !!supplementalInfo.tripinfo;
}

export {
    selectCarrierCollection,
    selectCarrierData,
    selectRequiresSupplementalTripInfo,
};
